import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Typography,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import Item from "../components/Item";
import AdminTutorBedrockLog from "./AdminTutorBedrockLog";
import { listTutorStories } from "./AdminTutorService";

function formatWhen(value) {
    if (!value) {
        return "—";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return String(value);
    }
    return date.toLocaleString();
}

export default function AdminTutorBoardPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const tab = searchParams.get("tab") === "history" ? "history" : "board";
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [status, setStatus] = useState("ALL");
    const [keyword, setKeyword] = useState("");
    const [testAccountFilter, setTestAccountFilter] = useState("exclude");
    const [excludeStaff, setExcludeStaff] = useState(true);
    const [openRow, setOpenRow] = useState(null);

    const selectTab = (_event, next) => {
        if (next === "history") {
            setSearchParams({ tab: "history" });
        } else {
            setSearchParams({});
        }
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const list = await listTutorStories();
                if (!cancelled) {
                    setRows(list);
                    setError("");
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err?.message || "Could not load tutor stories.");
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const visible = useMemo(() => {
        const query = keyword.trim().toLowerCase();
        return rows.filter((row) => {
            if (status !== "ALL" && row.status !== status) {
                return false;
            }
            if (testAccountFilter === "exclude" && row.testAccount) {
                return false;
            }
            if (testAccountFilter === "include" && !row.testAccount) {
                return false;
            }
            if (excludeStaff && row.staff) {
                return false;
            }
            if (!query) {
                return true;
            }
            const haystack = [row.title, row.ownerName, row.ownerEmail, row.organizationName]
                .join(" ")
                .toLowerCase();
            return haystack.includes(query);
        });
    }, [excludeStaff, keyword, rows, status, testAccountFilter]);

    return (
        <Item>
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
                Tutor
            </Typography>
            <Tabs
                value={tab}
                onChange={selectTab}
                sx={{ mb: 2, minHeight: 44, "& .MuiTab-root": { minHeight: 44, textTransform: "none" } }}
            >
                <Tab label="Board" value="board" />
                <Tab label="History" value="history" />
            </Tabs>
            {tab === "history" ? (
                <AdminTutorBedrockLog topSpacing={0} />
            ) : (
            <>
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center", mb: 2 }}>
                <TextField
                    label="Search"
                    value={keyword}
                    onChange={(event) => setKeyword(event.target.value)}
                    size="small"
                    sx={{ minWidth: 220 }}
                />
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel id="tutor-board-status">Status</InputLabel>
                    <Select
                        labelId="tutor-board-status"
                        label="Status"
                        value={status}
                        onChange={(event) => setStatus(event.target.value)}
                    >
                        <MenuItem value="ALL">All</MenuItem>
                        <MenuItem value="ACTIVE">Active</MenuItem>
                        <MenuItem value="DELETED">Deleted</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel id="tutor-board-test-filter">Test accounts</InputLabel>
                    <Select
                        labelId="tutor-board-test-filter"
                        label="Test accounts"
                        value={testAccountFilter}
                        onChange={(event) => setTestAccountFilter(event.target.value)}
                    >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="exclude">Exclude tester</MenuItem>
                        <MenuItem value="include">Include tester</MenuItem>
                    </Select>
                </FormControl>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={excludeStaff}
                            onChange={(event) => setExcludeStaff(event.target.checked)}
                            color="primary"
                        />
                    }
                    label="Exclude staff"
                />
            </Box>
            {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Title</TableCell>
                            <TableCell>Owner</TableCell>
                            <TableCell>Organization</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Created</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {visible.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6}>No stories.</TableCell>
                            </TableRow>
                        ) : (
                            visible.map((row) => (
                                <TableRow key={`${row.ownerId}-${row.id}`}>
                                    <TableCell>{row.title}</TableCell>
                                    <TableCell>
                                        <Box>{row.ownerName || "—"}</Box>
                                        {row.ownerEmail ? (
                                            <Typography variant="caption" color="text.secondary">
                                                {row.ownerEmail}
                                            </Typography>
                                        ) : null}
                                    </TableCell>
                                    <TableCell>{row.organizationName || "—"}</TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={row.status === "DELETED" ? "DELETED" : "ACTIVE"}
                                            color={row.status === "DELETED" ? "default" : "success"}
                                        />
                                    </TableCell>
                                    <TableCell>{formatWhen(row.createdAt)}</TableCell>
                                    <TableCell>
                                        <Button
                                            size="small"
                                            onClick={() => setOpenRow(row)}
                                            sx={{ minHeight: 44, textTransform: "none" }}
                                        >
                                            Open
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            )}
            <Dialog open={Boolean(openRow)} onClose={() => setOpenRow(null)} fullWidth maxWidth="sm">
                <DialogTitle>{openRow?.title}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {openRow?.ownerName}
                        {openRow?.ownerEmail ? ` · ${openRow.ownerEmail}` : ""}
                        {openRow?.organizationName ? ` · ${openRow.organizationName}` : ""}
                    </Typography>
                    <Chip
                        size="small"
                        label={openRow?.status === "DELETED" ? "DELETED" : "ACTIVE"}
                        sx={{ mb: 2 }}
                    />
                    {openRow?.status === "DELETED" ? (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Hidden from the tutor. This page is the only way to open it.
                        </Alert>
                    ) : null}
                    <Typography sx={{ whiteSpace: "pre-wrap", lineHeight: 1.7 }}>
                        {openRow?.sourceText || ""}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenRow(null)} sx={{ minHeight: 44, textTransform: "none" }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
            </>
            )}
        </Item>
    );
}
